/**
 * SitemapInteractionContext — transient editor interactions for page nodes,
 * shared from InnerCanvas (which owns the xyflow store + node/edge setters)
 * down to PageNode across xyflow's node-renderer boundary.
 *
 * Holds NO persisted state: `openNodeMenu` lets a node open its action menu
 * (rendered by the canvas), and `renameSignal` is a fire-once bump the canvas
 * sets when "Rename" is chosen so PageNode can enter its inline-edit state —
 * deliberately NOT written to node data (which would persist into Yjs).
 *
 * Safe outside a provider (inert), so PageNode also works on PreviewCanvas.
 */
import { createContext, useContext, type PointerEvent } from "react";
import type { Breakpoint } from "./breakpoints.js";

/** Args a grid item passes to begin a cross-grid drag (grid 0.8.42). */
export interface CrossGridStartArgs {
  itemId: string;
  fromGridId: string;
  /** "app" = a leaf widget (moves between section grids); "container" = a
   *  section (moves between page grids). Feeds the grid's targeting + accepts. */
  kind: "app" | "container";
}

export interface SitemapInteraction {
  /** Open the page-action menu for `nodeId` anchored at viewport (x,y). */
  readonly openNodeMenu: (nodeId: string, x: number, y: number) => void;
  /** Open the "Change URL…" slug editor for `nodeId`, anchored at viewport
   *  (x,y). Fired by the URL pencil on the selected page (web). */
  readonly openSlugEditor: (
    nodeId: string,
    x: number,
    y: number,
    label: string,
    slug?: string,
  ) => void;
  /** During a drag-to-parent, the page the drop targets (the highlighted node),
   *  or null. Transient — never persisted. */
  readonly dropParentId: string | null;
  /** The relationship the drop would make with `dropParentId`: "child" = the
   *  dragged page nests under it (dropped on its bottom half); "parent" = the
   *  dragged page becomes its parent (dropped on its top half). */
  readonly dropMode: "parent" | "child";
  /** The device the canvas is currently authoring for — drives every page's
   *  authoring width and device chrome. A view state (not persisted). */
  readonly breakpoint: Breakpoint;
  /** Begin a cross-grid drag of a grid item — the canvas mounts the grid's web
   *  adapter (useWebCrossGridDrag) once and shares its starter down here so an
   *  item's pointer-down can hand off to it. Inert (no-op) outside a provider or
   *  in read-only preview. Passed via context (NOT a module singleton) because
   *  the bundler may split the editor across chunks. */
  readonly startCrossGridDrag: (
    e: PointerEvent,
    args: CrossGridStartArgs,
  ) => void;
}

const INERT: SitemapInteraction = {
  openNodeMenu: () => {},
  openSlugEditor: () => {},
  dropParentId: null,
  dropMode: "child",
  breakpoint: "desktop",
  startCrossGridDrag: () => {},
};

const SitemapInteractionContext = createContext<SitemapInteraction | null>(
  null,
);

export const SitemapInteractionProvider = SitemapInteractionContext.Provider;

export function useSitemapInteraction(): SitemapInteraction {
  return useContext(SitemapInteractionContext) ?? INERT;
}
